import { FREE_RANGE_TOKEN } from './buildSearchParams'
import {
  ADVANCED_FILTER,
  ASSET_TYPE,
  CONTENT_SOURCE,
  DATE_PUBLISHED,
  LICENSE_FAMILIES,
  PERSONAL_FILTER,
  PRICE_RANGE,
  POLYGON_RANGE,
  PURCHASE_FILTER,
  SORT_BY,
  SUBSCRIPTION_TIER,
} from './constants'
import { splitKeywordTerms } from './keywordTerms'
import { SCOPE_PARAMS } from '../Shared/DesignerContent/constants'
import { findStyleLabel } from '../Shared/StyleContent/mock'

const PRICE_BUCKETS = new Set(Object.values(PRICE_RANGE))
const POLYGON_PRESETS = new Set(Object.values(POLYGON_RANGE))
const ADVANCED_FILTERS = new Set(Object.values(ADVANCED_FILTER))
const PERSONAL_FILTERS = new Set(Object.values(PERSONAL_FILTER))
const CONTENT_SAFETY_LICENSES = new Set(['exclude_adult_content'])
const SORT_VALUES = new Set(Object.values(SORT_BY))
const SUBSCRIPTION_TIER_BANDS = new Set([SUBSCRIPTION_TIER.ESSENTIALS, SUBSCRIPTION_TIER.PREMIUM])
const DATE_PRESETS = new Set(
  Object.values(DATE_PUBLISHED).filter(
    (value) => value !== DATE_PUBLISHED.ALL_TIME && value !== DATE_PUBLISHED.CUSTOM
  )
)
// Every variant ticks its family's checkbox, so old links (plain, `_no_ai`, or the removed AI Usage
// toggle's full AI-friendly set) keep their selection.
const LICENSE_FAMILY_BY_VALUE = new Map(
  Object.entries(LICENSE_FAMILIES).flatMap(([family, values]) => values.map((value) => [value, family]))
)

const CUSTOM_RANGE_PARSER = /^range_(\d+)_(\d+)$/
const MORE_THAN_PARSER = /^more_than_(\d+)$/
const PUBLISHED_RANGE_PARSER = /^range_(\d{4}-\d{2}-\d{2})_(\d{4}-\d{2}-\d{2})$/

// `\d{2}` also matches impossible calendar days (2025-13-40, 2025-02-30, which JS silently rolls
// over to 2025-03-02). The backend drops those with Date.iso8601, and it drops inverted ranges
// too, so accepting them here would select Custom in the sidebar while SSR results stay
// unfiltered. Round-trip the parsed date to reject anything the backend will not honour.
const isCalendarDay = (value) => {
  const parsed = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value)
}

// Boolean query params buildSearchParams emits as '1', keyed by their store field.
const BOOLEAN_PARAMS = {
  sale_off: 'onSale',
  subscribed: 'withSubscription',
  animated: 'animated',
  pbr: 'pbr',
  rigged: 'rigged',
  game_ready: 'gameReady',
  with_renders: 'withRenders',
  with_makes: 'withMakes',
  cgt_verified: 'threeDPrintVerified',
  pre_supported: 'preSupported',
  multiple_parts: 'multipleParts',
}

const decode = (part) => {
  try {
    return decodeURIComponent(part.replace(/\+/g, ' '))
  } catch (e) {
    return part
  }
}

// Minimal parser for the Rails `to_query` format the backend echoes into the canonical URL
// (`key=v`, `key[]=v` arrays, `price[from]=0` nested scalars). URLSearchParams is not available
// in the SSR runtime, and this must parse identically on server and client.
const parseQuery = (search) => {
  const params = {}
  search.split('&').filter(Boolean).forEach((pair) => {
    const eq = pair.indexOf('=')
    const key = decode(eq === -1 ? pair : pair.slice(0, eq))
    const value = eq === -1 ? '' : decode(pair.slice(eq + 1))
    if (key.endsWith('[]')) {
      const base = key.slice(0, -2)
      params[base] = params[base] || []
      params[base].push(value)
    } else {
      params[key] = value
    }
  })
  return params
}

// parseQuery only builds arrays for `key[]=` params. A hand-written or legacy URL can carry the
// scalar form (`?price_range=range_2_10`), so every array-capable param is normalized before it is
// iterated - otherwise hydration throws on `String.filter`/`forEach` and takes the SSR render with it.
const toArray = (value) => {
  if (value === undefined || value === '') return []
  return Array.isArray(value) ? value : [value]
}

// Splits range tokens into { buckets, min, max }: tokens found in `known` are preset buckets,
// `range_MIN_MAX` / `more_than_MIN` are the custom range (mirrors what buildSearchParams emits).
const parseRangeTokens = (tokens, known) => {
  const result = { buckets: [], min: undefined, max: undefined }
  tokens.forEach((token) => {
    if (known.has(token)) {
      result.buckets.push(token)
      return
    }
    const range = token.match(CUSTOM_RANGE_PARSER)
    const moreThan = token.match(MORE_THAN_PARSER)
    if (range) {
      if (Number(range[1]) > 0) result.min = Number(range[1])
      result.max = Number(range[2])
    } else if (moreThan) {
      result.min = Number(moreThan[1])
    }
  })
  return result
}

const hydratePrice = (params) => {
  const state = {}
  // Free travels as `range_0_0` inside price_range when combined with other ranges (see
  // buildSearchParams FREE_RANGE_TOKEN), or as price={0,0} when selected alone. Pull the token
  // out before generic parsing - CUSTOM_RANGE_PARSER would otherwise read it as maxPrice=0.
  const priceRange = toArray(params.price_range)
  const tokens = priceRange.filter((token) => token !== FREE_RANGE_TOKEN)
  const hasFreeToken = tokens.length !== priceRange.length
  const { buckets, min, max } = parseRangeTokens(tokens, PRICE_BUCKETS)

  // `free=1` is backend-derived: SearchKeywordToFilter canonicalizes "free" keywords (and ?free=1
  // links) into it and SSR returns only free models. It must hydrate into the Free bucket or the
  // next fetch silently drops the constraint and returns paid models.
  if (hasFreeToken || params.free === '1' || (params['price[from]'] === '0' && params['price[to]'] === '0')) {
    buckets.unshift(PRICE_RANGE.FREE)
  }

  if (buckets.length > 0) state.priceRanges = buckets
  if (min !== undefined) state.minPrice = min
  if (max !== undefined) state.maxPrice = max
  return state
}

const hydratePolygon = (params) => {
  const state = {}
  const { buckets, min, max } = parseRangeTokens(toArray(params.polygon_range), POLYGON_PRESETS)

  if (buckets.length > 0) state.polygonRanges = buckets
  if (min !== undefined) state.minPolygon = min
  if (max !== undefined) state.maxPolygon = max
  return state
}

// The date filter is single-valued (radio + one custom range), so the first recognised token wins.
const hydratePublishedRange = (params) => {
  const state = {}
  toArray(params.published_range).some((token) => {
    if (DATE_PRESETS.has(token)) {
      state.datePublished = token
      return true
    }
    const range = token.match(PUBLISHED_RANGE_PARSER)
    if (range && isCalendarDay(range[1]) && isCalendarDay(range[2]) && range[1] <= range[2]) {
      state.datePublished = DATE_PUBLISHED.CUSTOM
      state.minDatePublished = range[1]
      state.maxDatePublished = range[2]
      return true
    }
    return false
  })
  return state
}

const hydrateLicenses = (params) => {
  const state = {}
  const advancedFilters = []
  const personalFilters = []
  const contentSafetyLicenses = []
  const licenseTypes = []
  toArray(params.licenses).forEach((token) => {
    if (token === 'ai_generated_only') {
      state.contentSource = CONTENT_SOURCE.AI_GENERATED
    } else if (token === 'exclude_ai_generated') {
      state.contentSource = CONTENT_SOURCE.HUMAN_CREATED
    } else if (token === 'include_ai_generated') {
      state.contentSource = CONTENT_SOURCE.ALL
    } else if (token === 'exclude_3d_print') {
      state.assetType = ASSET_TYPE.THREE_D_ONLY
    } else if (PERSONAL_FILTERS.has(token)) {
      if (!personalFilters.includes(token)) personalFilters.push(token)
    } else if (token === 'owned_models') {
      state.purchaseFilter = PURCHASE_FILTER.PURCHASED_MODELS_ONLY
    } else if (ADVANCED_FILTERS.has(token)) {
      advancedFilters.push(token)
    } else if (CONTENT_SAFETY_LICENSES.has(token)) {
      // No control exposes these, but /3d-models/trending and the print sort routes apply
      // exclude_adult_content as a route default; dropping it here would switch datasets on the
      // first refetch (see DEFAULT_STATE.contentSafetyLicenses).
      contentSafetyLicenses.push(token)
    } else if (LICENSE_FAMILY_BY_VALUE.has(token)) {
      const family = LICENSE_FAMILY_BY_VALUE.get(token)
      if (!licenseTypes.includes(family)) licenseTypes.push(family)
    }
    // Unknown tokens are dropped: re-emitting them would let arbitrary URL input round-trip
    // into every subsequent request.
  })

  if (advancedFilters.length > 0) state.advancedFilters = advancedFilters
  if (personalFilters.length > 0) state.personalFilters = personalFilters
  if (contentSafetyLicenses.length > 0) state.contentSafetyLicenses = contentSafetyLicenses
  if (licenseTypes.length > 0) state.licenses = licenseTypes
  return state
}

/**
 * Inverse of buildSearchParams: maps the canonical listing URL's query params back to SearchPage
 * store state, so landing on a filtered URL seeds the filters that produced it. Later fetches then
 * rebuild the same params instead of silently dropping them.
 *
 * Parses only params buildSearchParams can emit; everything else (keywords, page, per_page,
 * unknown keys) is ignored. `keywords` intentionally stays URL-only (see Store/actions/results.js).
 *
 * Two filters live in the URL path rather than its query: the print listing scope
 * (/3d-print-models/...) and the file type a software page (/3d-models/3ds-max) applies. The caller
 * resolves those and passes them as `pathState`; they are applied first, so a query param for the
 * same field still wins.
 * `fileTypes` is the exception: the backend adds the software page's format to the query's
 * file_types (ExtractItemsSearchOptions#extract_file_types), so the two are unioned here.
 *
 * @param {string} url - Path with query string (the backend-provided canonical `url` prop)
 * @param {Object} [pathState] - Filters the URL path encodes (`assetType`, `fileTypes`)
 * @returns {Object} Partial store state; only `pathState` when the URL carries no supported filters
 */
const hydrateStateFromUrl = (url = '', pathState = {}) => {
  const queryIndex = url.indexOf('?')
  if (queryIndex === -1) return { ...pathState }

  const params = parseQuery(url.slice(queryIndex + 1))
  const state = {
    ...pathState,
    ...hydratePrice(params),
    ...hydratePolygon(params),
    ...hydratePublishedRange(params),
    ...hydrateLicenses(params),
  }

  if (params.printable === '1') state.assetType = ASSET_TYPE.THREE_D_PRINT_ONLY
  const fileTypes = [...new Set([...(pathState.fileTypes || []), ...toArray(params.file_types)])]
  if (fileTypes.length > 0) state.fileTypes = fileTypes
  // `author` carries designer usernames. toArray covers both shapes: the repeated `author[]=` the
  // multi-select emits, and the legacy scalar `?author=joe` that profile links still use.
  const authors = toArray(params.author)
  if (authors.length > 0) state.designers = authors
  // The scope flags come back as whole-scope selections; a scope the user had partly unticked, or
  // one without a flag, was written out as plain `author[]` instead and hydrates as individual picks.
  const scopes = Object.keys(SCOPE_PARAMS).filter(scope => params[SCOPE_PARAMS[scope]])
  if (scopes.length > 0) state.designerScopes = scopes
  // at_style carries lowercase ES tokens; the store keeps display labels ("sci-fi" -> "Sci-Fi").
  // Tokens outside the known style lists are dropped like any other unknown URL input.
  const atStyle = toArray(params.at_style)
  if (atStyle.length > 0) {
    const styles = atStyle.map(findStyleLabel).filter(Boolean)
    if (styles.length > 0) state.styles = styles
  }
  if (params.suggested_keywords) state.tags = params.suggested_keywords.split(',').filter(Boolean)
  const withinKeywords = toArray(params.within_keywords).join(' ')
  if (withinKeywords) state.keywordTerms = splitKeywordTerms(withinKeywords)
  if (SORT_VALUES.has(params.sort_by)) state.sortBy = params.sort_by

  Object.entries(BOOLEAN_PARAMS).forEach(([param, field]) => {
    if (params[param] === '1') state[field] = true
  })

  // The backend ignores the band without `subscribed`, so a lone band must not select a tier.
  if (state.withSubscription && SUBSCRIPTION_TIER_BANDS.has(params.subscription_tier_band)) {
    state.subscriptionTiers = [params.subscription_tier_band]
  }

  // `low_poly` is the legacy input name for the same backend filter as `game_ready` (see
  // ItemsSearch::Options#game_ready_filter) and still reaches this page - SearchKeywordToFilter
  // injects it for "low poly" keywords and echoes it into the canonical URL. Without it the SSR
  // results would be game-ready filtered while the next client fetch drops the filter. Note it
  // maps to gameReady, not to the sidebar's own "Low Poly" chip - that one is a poly-count bucket
  // (polygon_range=lt_20k), a different filter entirely.
  //
  // Precedence mirrors game_ready_filter, which is `game_ready.nil? ? low_poly : game_ready`: any
  // `game_ready` in the URL wins outright, including an empty one (which leaves the backend filter
  // unset), so `low_poly` is read only when the key is absent altogether.
  if (params.game_ready === undefined && params.low_poly === '1') state.gameReady = true

  return state
}

export default hydrateStateFromUrl
